import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";

// Depot coordinates — key operational nodes on the African corridor
export const DEPOT_COORDS = [
  { key: "south-africa", lat: -26.2041, lng: 28.0473 },   // Johannesburg
  { key: "namibia", lat: -22.9576, lng: 14.5052 },        // Walvis Bay
  { key: "mozambique", lat: -19.8437, lng: 34.8389 },     // Beira
  { key: "tanzania", lat: -6.7924, lng: 39.2083 },        // Dar es Salaam
  { key: "botswana", lat: -24.6282, lng: 25.9231 },       // Gaborone
  { key: "drc", lat: -11.6608, lng: 27.4794 },            // Lubumbashi (Cu-Co belt)
];

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const makeIcon = (active) =>
  L.divIcon({
    className: "vv-depot-marker",
    html: `<div class="vv-marker-ring ${active ? "is-active" : ""}"><div class="vv-marker-core"></div></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

const DepotMap = ({ depots, activeIndex, onSelect, labels }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const enriched = useMemo(
    () =>
      depots.map((d, i) => {
        const coord = DEPOT_COORDS.find((c) => c.key === slug(d.region)) || { lat: 0, lng: 20, key: `depot-${i}` };
        return { ...d, ...coord };
      }),
    [depots]
  );

  // Initialise Leaflet map once — StrictMode-safe via container guard + cleanup
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    // Guard: if leaflet already bound this node, do not re-init
    if (node._leaflet_id != null && mapRef.current) return;

    const map = L.map(node, {
      center: [-6, 22],
      zoom: 3,
      minZoom: 3,
      maxZoom: 7,
      scrollWheelZoom: false,
      zoomControl: false,
      attributionControl: false,
    });
    mapRef.current = map;

    L.tileLayer(
      "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_nolabels/{z}/{x}/{y}.png",
      { subdomains: ["a", "b", "c", "d"], noWrap: false }
    ).addTo(map);

    return () => {
      try { map.remove(); } catch { /* noop */ }
      mapRef.current = null;
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers whenever depot list or active index changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // clear existing markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = enriched.map((d, i) => {
      const marker = L.marker([d.lat, d.lng], { icon: makeIcon(i === activeIndex) });
      marker.on("click", () => onSelect(i));
      marker.addTo(map);
      return marker;
    });
  }, [enriched, activeIndex, onSelect]);

  // Fly to active marker
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const active = enriched[activeIndex];
    if (!active) return;
    map.flyTo([active.lat, active.lng], Math.max(map.getZoom(), 5), { duration: 1.0 });
  }, [activeIndex, enriched]);

  const zoomIn = () => mapRef.current && mapRef.current.zoomIn();
  const zoomOut = () => mapRef.current && mapRef.current.zoomOut();

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="depot-map-container">
      {/* Map */}
      <div className="lg:col-span-8 relative border border-[#D4AF37]/25 overflow-hidden" style={{ height: 560, background: "#020C1B" }}>
        <div ref={containerRef} style={{ height: "100%", width: "100%", background: "#020C1B" }} data-testid="depot-leaflet-node" />

        {/* Corner brand */}
        <div className="pointer-events-none absolute top-4 left-4 z-[500] flex items-center gap-2 bg-[#0A192F]/85 backdrop-blur-sm border border-[#D4AF37]/25 px-3 py-2">
          <div className="w-1.5 h-1.5 bg-[#D4AF37]" />
          <span className="font-sans text-[10px] uppercase text-[#D4AF37]" style={{ letterSpacing: "0.28em" }}>
            {labels.mapTitle}
          </span>
        </div>
        <div className="pointer-events-none absolute bottom-3 right-4 z-[500]">
          <span className="font-sans text-[9px] text-steel/60" style={{ letterSpacing: "0.14em" }}>
            © OpenStreetMap · CARTO
          </span>
        </div>

        {/* Custom zoom controls */}
        <div className="absolute top-4 right-4 z-[500] flex flex-col border border-[#D4AF37]/25 bg-[#0A192F]/90 backdrop-blur-sm">
          <button
            type="button"
            onClick={zoomIn}
            data-testid="map-zoom-in"
            className="w-9 h-9 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors border-b border-[#D4AF37]/20 font-serif text-lg"
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            type="button"
            onClick={zoomOut}
            data-testid="map-zoom-out"
            className="w-9 h-9 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors font-serif text-lg"
            aria-label="Zoom out"
          >
            −
          </button>
        </div>
      </div>

      {/* Depot rail */}
      <div className="lg:col-span-4 flex flex-col gap-3" data-testid="depot-rail">
        {enriched.map((d, i) => {
          const isActive = i === activeIndex;
          return (
            <button
              key={d.key}
              type="button"
              data-testid={`depot-rail-item-${d.key}`}
              onClick={() => onSelect(i)}
              className={`text-left p-4 border transition-all duration-300 ${
                isActive
                  ? "bg-[#0A192F] border-[#D4AF37] shadow-[0_0_0_1px_rgba(212,175,55,0.35)]"
                  : "bg-[#0A192F]/60 border-[#D4AF37]/15 hover:border-[#D4AF37]/50 hover:bg-[#0A192F]"
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="font-sans text-[10px] uppercase text-steel" style={{ letterSpacing: "0.24em" }}>
                  {labels.node} · 0{i + 1}
                </span>
                <div className="flex items-center gap-2">
                  <span className="status-dot" aria-hidden />
                  <span className="font-sans text-[9px] uppercase tracked text-[#4ADE80]" style={{ letterSpacing: "0.24em" }}>
                    {labels.active}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-1 text-[#D4AF37]">
                <MapPin size={12} />
                <span className="font-sans text-[10px] uppercase" style={{ letterSpacing: "0.22em" }}>
                  {d.node}
                </span>
              </div>
              <h4 className="font-serif text-xl text-bone leading-tight" style={{ fontWeight: 500 }}>
                {d.region}
              </h4>
              {isActive && (
                <p className="font-sans text-[13px] text-steel leading-relaxed mt-3">
                  {d.body}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DepotMap;
